import { getApps } from "firebase/app";
import type { User } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirebaseAuth } from "@/auth/firebase";

const SESSION_SYNC_PREFIX = "mind-survival/gm-claims-synced/v1";

function sessionSyncKey(userId: string): string {
  return `${SESSION_SYNC_PREFIX}:${userId}`;
}

/** อ่าน claims จาก token ในเครื่อง — ไม่เรียก Cloud Function */
export async function readGmClaims(user: User): Promise<Record<string, unknown>> {
  const token = await user.getIdTokenResult(false);
  return token.claims as Record<string, unknown>;
}

function markSessionSynced(userId: string): void {
  try {
    sessionStorage.setItem(sessionSyncKey(userId), "1");
  } catch {
    /* private mode */
  }
}

function wasSyncedThisSession(userId: string): boolean {
  try {
    return sessionStorage.getItem(sessionSyncKey(userId)) === "1";
  } catch {
    return false;
  }
}

export function resetGmClaimsSessionSync(userId: string): void {
  try {
    sessionStorage.removeItem(sessionSyncKey(userId));
  } catch {
    /* private mode */
  }
}

/** เรียก syncGmClaims ทุกครั้ง แล้ว refresh token */
export async function syncGmClaimsNow(user: User): Promise<Record<string, unknown>> {
  const auth = getFirebaseAuth();
  if (!auth) {
    return readGmClaims(user);
  }

  const app = getApps()[0];
  if (!app) {
    return readGmClaims(user);
  }

  try {
    const functions = getFunctions(app);
    const sync = httpsCallable<void, { gm: boolean; owner: boolean }>(functions, "syncGmClaims");
    await sync();
    markSessionSynced(user.uid);
    const token = await user.getIdTokenResult(true);
    return token.claims as Record<string, unknown>;
  } catch {
    return readGmClaims(user);
  }
}

/**
 * เรียก syncGmClaims สูงสุด 1 ครั้งต่อ tab/session ต่อ user
 * หลัง sync สำเร็จจะ refresh token ครั้งเดียว
 */
export async function ensureGmClaimsSynced(user: User): Promise<Record<string, unknown>> {
  if (wasSyncedThisSession(user.uid)) {
    return readGmClaims(user);
  }

  const auth = getFirebaseAuth();
  if (!auth) {
    return {};
  }

  const app = getApps()[0];
  if (app) {
    try {
      const functions = getFunctions(app);
      const sync = httpsCallable<void, { gm: boolean; owner: boolean }>(
        functions,
        "syncGmClaims",
      );
      await sync();
      markSessionSynced(user.uid);
      const token = await user.getIdTokenResult(true);
      return token.claims as Record<string, unknown>;
    } catch {
      // Functions ยังไม่มี — ใช้ token เดิม
    }
  }

  return readGmClaims(user);
}
